import { getErrorMessage } from "../utils/Utils";

export type CommonDataType = {
  url: string;
  config?: RequestInit;
};

export type PostDataType<TRequest> = CommonDataType & {
  body: TRequest;
};

export type ApiFetchOptions = {
  url: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: any;
  config?: RequestInit;
};

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "ApiError";
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export async function apiFetch<TResponse>({ url, method = "GET", body, config }: ApiFetchOptions): Promise<TResponse> {
  try {
    const headers: HeadersInit = {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...config?.headers,
    };

    const response: Response = await fetch(url, {
      credentials: "include",
      ...config,
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    if (!response.ok) {
      throw new ApiError(
        response.status === 401
          ? "Unauthorized"
          : `Request failed with status ${response.status}: ${response.statusText}`,
        response.status
      );
    }

    const text = await response.text();
    return (text ? JSON.parse(text) : {}) as TResponse;
  } catch (error) {
    console.error(getErrorMessage(error));
    throw error;
  }
}

export async function postData<TRequest, TResponse>({ url, body, config }: PostDataType<TRequest>): Promise<TResponse> {
  return apiFetch<TResponse>({ url, method: "POST", body, config });
}

export async function getData<TResponse>({ url, config }: CommonDataType): Promise<TResponse> {
  return apiFetch<TResponse>({ url, method: "GET", config });
}
